import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { LastNMatchesResponse, Match, MatchDay, MatchLineup } from '@core/models/match.models'
import { environment } from '@env'
import { from, Observable, of } from 'rxjs'
import { catchObsError } from '@shared/decorators/catch-obs-error.decorator'
import { MetricMap } from '@core/models/metric.models'
import { PassMatrix } from '@pages/match-detail/models/match-detail.models'
import { GreenKeeper, KitSelector, Sunlight } from '@pages/match-detail/models/match-detail-info.models'
import { Aggregation } from '@features/aggregations/models/aggregations.models'
import { MatchStream, MatchStreaming } from '@core/models/match.base.models'
import { Heatmap } from '@core/models/heatmap.models'
import { Platform } from '@ionic/angular'
import { CapacitorHttp } from '@capacitor/core'
import { MatchAsset } from '@core/models/assets.models'
import { MatchStreamEvents } from '@domain/match-events.models'
import { Referee } from '@core/models/referee.models'

@Injectable({ providedIn: 'root' })
export class MatchApi {
  private readonly _http: HttpClient = inject(HttpClient)
  private readonly _platform: Platform = inject(Platform)

  fetchMatch(matchId: string) {
    return this._http.get<Match>(environment.api.matchesUrlFn(matchId))
  }

  fetchMatchesBetweenDates(seasonId: string, competitionId: string, dateFrom: string, dateTo: string) {
    return this._http.get<MatchDay[]>(
      environment.api.matchesBetweenDateUrlFn(seasonId, competitionId, dateFrom, dateTo)
    )
  }

  fetchMatchesByMatchDayId(matchDayId: string): Observable<{ matches: Match[] }> {
    return this._http.get<{ matches: Match[] }>(`${environment.api.matchDaysUrl}/${matchDayId}/matches`)
  }

  fetchLineup(
    seasonId: string,
    competitionId: string,
    matchId: string,
    aggregationDataSource: Aggregation
  ): Observable<MatchLineup> {
    return this._http.get<MatchLineup>(
      environment.api.matchPlayerListMetricsWithContextUrlFn(seasonId, competitionId, matchId),
      {
        params: { aggregationDataSource }
      }
    )
  }

  fetchMatchMetrics(
    seasonId: string,
    competitionId: string,
    matchId: string,
    leftTeamId: string,
    rightTeamId: string,
    leftAggregationDataSource: Aggregation,
    rightAggregationDataSource: Aggregation
  ): Observable<MetricMap> {
    return this._http.get<MetricMap>(
      environment.api.matchTeamMetricsWithContextUrlFn(seasonId, competitionId, matchId),
      {
        params: { rightAggregationDataSource, leftAggregationDataSource, leftTeamId, rightTeamId }
      }
    )
  }

  @catchObsError()
  fetchLastMatches(matchId: string): Observable<LastNMatchesResponse> {
    return this._http.get<LastNMatchesResponse>(environment.api.lastMatchesUrlFn(matchId))
  }

  fetchMatchPassMatrix(matchId: string): Observable<PassMatrix> {
    return this._http.get<PassMatrix>(environment.api.matchPassMatrixUrlFn(matchId))
  }

  fetchHeatMap(seasonId: string, competitionId: string, matchId: string): Observable<Heatmap> {
    return this._http.get<Heatmap>(environment.api.matchHeatmapWithContextUrlFn(seasonId, competitionId, matchId))
  }

  @catchObsError()
  fetchTimelineConfig(matchId: string): Observable<MatchStreamEvents> {
    return this._http.get<MatchStreamEvents>(environment.api.filterMatchesTimeline(matchId))
  }

  @catchObsError()
  fetchStream(matchId: string): Observable<MatchStream> {
    return this._http.get<MatchStream>(environment.api.matchStreamingUrlFn(matchId))
  }

  resolveStreamUrl(url: string, withCredentials: boolean): Observable<MatchStreaming> {
    if (!withCredentials) {
      return of({ url })
    }

    return this._platform.is('ios')
      ? from(
          CapacitorHttp.get({ url, headers: { Origin: environment.iOSFakeOrigin } }).then((response) => response.data)
        )
      : this._http.get<any>(url, { withCredentials })
  }

  fetchReferees(matchId: string): Observable<{ referees: Referee[]; cachedDateTime: Date }> {
    return this._http.get<{ referees: Referee[]; cachedDateTime: Date }>(environment.api.matchRefereeUrlFn(matchId))
  }

  fetchKitSelector(matchId: string): Observable<KitSelector> {
    return this._http.get<KitSelector>(environment.api.matchKitSelectorUrlFn(matchId))
  }

  fetchGreenKeeper(matchId: string): Observable<GreenKeeper> {
    return this._http.get<GreenKeeper>(environment.api.matchGreenKeeperUrlFn(matchId))
  }

  fetchSunlight(matchId: string): Observable<Sunlight> {
    return this._http.get<Sunlight>(environment.api.matchSunlightUrlFn(matchId))
  }

  fetchMatchAssets(matchId: string): Observable<{ matchesAssets: MatchAsset[] }> {
    return this._http.get<{ matchesAssets: MatchAsset[] }>(environment.api.assets, { params: { matchIds: matchId } })
  }
}
